import React, { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
type LogoVariant = {
  name: string;
  description: string;
  url: string;
  background: 'light' | 'dark';
};
const LOGOS: LogoVariant[] = [{
  name: 'Caveo Icon — Full Color',
  description: 'Standalone mark. Use for favicons, avatars, and tight spaces.',
  url: 'https://cdn.magicpatterns.com/uploads/2AUBZP8wJG1EJKucMuiKhM/Caveo_Icon_Full_Color.png',
  background: 'light'
}, {
  name: 'Caveo Logotype — Purple (Light Backgrounds)',
  description: 'Wordmark only. Use on white or light surfaces.',
  url: 'https://cdn.magicpatterns.com/uploads/h2g1uh2wjUTHUuCPxeSGhL/Caveo_Purple_Logotype_Light_Backgrounds.png',
  background: 'light'
}, {
  name: 'Caveo Logotype — White (Dark Backgrounds)',
  description: 'Wordmark only. Use on dark or photographic surfaces.',
  url: 'https://cdn.magicpatterns.com/uploads/kBJ7r4pC68rjDRjcV3JXtY/Caveo_White_Logotype_Dark_Backgrounds.png',
  background: 'dark'
}, {
  name: 'Caveo Horizontal Logo — Full Color',
  description: 'Primary lockup with icon + wordmark. Default for headers.',
  url: 'https://cdn.magicpatterns.com/uploads/7RvAqT2A1NNXJxeFUdoF8d/Caveo_Full_Color_Horizontal_Logo.png',
  background: 'light'
}];
function CopyButton({
  value
}: {
  value: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable; silently ignore
    }
  };
  return <button type="button" onClick={handleCopy} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-4055">
      {copied ? <>
          <CheckIcon size={13} className="text-green-600" data-id="element-4056" />
          Copied
        </> : <>
          <CopyIcon size={13} data-id="element-4057" />
          Copy URL
        </>}
    </button>;
}
function LogoCard({
  logo
}: {
  logo: LogoVariant;
}) {
  const isDark = logo.background === 'dark';
  return <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-4058">
      <div className={`flex items-center justify-center p-12 ${isDark ? 'bg-[#1a0647]' : 'bg-[#f9fafb]'}`} style={{
      minHeight: '240px'
    }} data-id="element-4059">
        <img src={logo.url} alt={logo.name} className="max-h-32 max-w-full object-contain" data-id="element-4060" />
      </div>

      <div className="p-5 border-t border-gray-100" data-id="element-4061">
        <h3 className="text-sm font-semibold text-[#111111] mb-1" data-id="element-4062">
          {logo.name}
        </h3>
        <p className="text-xs text-gray-500 mb-3" data-id="element-4063">{logo.description}</p>

        <div className="flex items-center gap-2" data-id="element-4064">
          <code className="flex-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5 truncate font-mono" data-id="element-4065">
            {logo.url}
          </code>
          <CopyButton value={logo.url} data-id="element-4066" />
        </div>

        <div className="mt-2" data-id="element-4067">
          <a href={logo.url} target="_blank" rel="noreferrer" className="text-xs text-[#631bb7] hover:underline font-medium" data-id="element-4068">
            Open in new tab ↗
          </a>
        </div>
      </div>
    </div>;
}
export function LogoLibraryPage() {
  return <main className="w-full min-h-screen bg-white" data-id="element-4069">
      <div className="max-w-5xl mx-auto px-6 py-12 md:py-16" data-id="element-4070">
        <header className="mb-10" data-id="element-4071">
          <p className="text-xs font-semibold tracking-wider uppercase text-[#631bb7] mb-2" data-id="element-4072">
            Design System · Test Page
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#111111] tracking-tight mb-3" data-id="element-4073">
            Caveo Logo Library
          </h1>
          <p className="text-base text-gray-500 max-w-2xl" data-id="element-4074">
            All approved Caveo logo variants. Click "Copy URL" to grab the asset
            link, or open the file in a new tab to download.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4075">
          {LOGOS.map(logo => <LogoCard key={logo.url} logo={logo} data-id="element-4076" />)}
        </section>
      </div>
    </main>;
}